import {tx, ptx, usePageText} from '../../lib/page-text';
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "../../lib/localized-router";
import { api } from "../../lib/api-client";
import { useAuth } from "../../lib/auth-store";
import { PlayerModal } from "../PlayerModal";
import { DashboardDialog } from "../DashboardDialog";
import { useProgramPermissions } from "./ProducerPanel";
const error = (e: unknown) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? "Could not load this replay. Try again.";
export function ReplayPublication({
  streamId,
  recordingId,
}: {
  streamId: string;
  recordingId: string;
}) {
  usePageText();
  const q = useProgramPermissions(streamId),
    qc = useQueryClient(),
    canEdit = useAuth((s) =>
      ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? ""),
    );
  const [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const published =
    q.data?.config.replays.published.includes(recordingId) ?? false;
  async function change() {
    if (!q.data) return;
    if (
      !window.confirm(
        published
          ? "Remove this replay from the audience page? Previously issued playback links can work for up to five minutes."
          : "Publish this recording and its saved board history to this stream’s audience? The stream’s viewing access settings apply.",
      )
    )
      return;
    setBusy(true);
    setNotice("");
    try {
      await api.patch(
        `/me/streams/${streamId}/program/replays/${recordingId}`,
        { version: q.data.version, published: !published },
      );
      await qc.invalidateQueries({ queryKey: ["program", streamId] });
      setNotice(published ? "Removed from audience" : "Published to audience");
    } catch (e) {
      setNotice(error(e));
      await q.refetch();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      {canEdit && (
        <button
          className="btn outline sm"
          disabled={busy || !q.data}
          onClick={() => void change()}
        >
          {busy
            ? tx("Saving…")
            : published
              ? tx("Unpublish replay")
              : tx("Publish for audience")}
        </button>
      )}
      {!canEdit && published && (
        <span className="badge">{tx("Published to audience")}</span>
      )}
      {q.isError && (
        <button className="btn ghost sm" onClick={() => void q.refetch()}>{tx("Retry publication status")}</button>
      )}
      {notice && <p role="status">{notice}</p>}
    </div>
  );
}
export function AudienceReplayPlayer({
  streamKey,
  recordingId,
  token,
  onClose,
}: {
  streamKey: string;
  recordingId: string;
  token?: string;
  onClose: () => void;
}) {
  usePageText();
  const prefix = `/streams/${encodeURIComponent(streamKey)}/program/replays/${encodeURIComponent(recordingId)}`;
  const access = useQuery({
    queryKey: ["replay-access", streamKey, recordingId, token],
    queryFn: async () =>
      (
        await api.get<{ id: string; title: string }>(prefix, {
          params: { t: token },
        })
      ).data,
    refetchInterval: 15000,
    retry: false,
  });
  const media = useQuery({
    queryKey: ["public-replay-media", streamKey, recordingId, token],
    enabled: !!access.data && !access.isError,
    queryFn: async () =>
      (
        await api.get<{ url: string; format: "mp4"; title: string }>(
          prefix + "/playback",
          { params: { t: token } },
        )
      ).data,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  if (access.isError || !media.data)
    return (
      <DashboardDialog title={tx("Stream replay")} onClose={onClose}>
        <p role={access.isError || media.isError ? "alert" : "status"}>
          {access.isError
            ? error(access.error)
            : media.isError
              ? error(media.error)
              : tx("Loading replay…")}
        </p>
        {(access.isError || media.isError) && (
          <button
            className="btn outline"
            onClick={() => {
              void access.refetch();
              void media.refetch();
            }}
          >{tx("Retry replay")}</button>
        )}
      </DashboardDialog>
    );
  return (
    <PlayerModal
      url={media.data.url}
      format={media.data.format}
      title={media.data.title}
      recordingId={recordingId}
      publicReplay={{ key: streamKey, token }}
      onClose={onClose}
      onRetry={() => void media.refetch()}
      refreshing={media.isFetching}
      retryError={media.isError ? error(media.error) : undefined}
      revision={media.dataUpdatedAt}
    />
  );
}
export function PublicReplayShelf({
  streamKey,
  token,
}: {
  streamKey: string;
  token?: string;
}) {
  usePageText();
  const q = useQuery({
    queryKey: ["public-replays", streamKey, token],
    queryFn: async () =>
      (
        await api.get<{ id: string; durationMs: number; createdAt: string }[]>(
          `/streams/${encodeURIComponent(streamKey)}/program/replays`,
          { params: { t: token } },
        )
      ).data,
    refetchInterval: 15000,
    retry: false,
  });
  const [playing, setPlaying] = useState<string | null>(null),
    lastTrigger = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const receive = (e: MessageEvent) => {
      if (
        e.origin === location.origin &&
        e.source === window.parent &&
        e.data?.type === "kardolive-replay-closed"
      )
        lastTrigger.current?.focus();
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, []);
  if (q.isError)
    return (
      <p className="muted">{ptx("Replays could not refresh.{v0}{v1}", {v0: (" "), v1: (<button className="btn ghost sm" onClick={() => void q.refetch()}>{tx("Retry replays")}</button>)})}</p>
    );
  if (!q.data?.length) return null;
  return (
    <section className="card public-replays">
      <h3>{tx("Replays")}</h3>
      {q.data.map((r) => (
        <article className="program-item" key={r.id}>
          <p>{ptx("{v0} ·{v1}{v2} min", {v0: (<bdi>{new Date(r.createdAt).toLocaleString()}</bdi>), v1: (" "), v2: (Math.ceil(r.durationMs / 60000))})}</p>
          <button
            className="btn outline"
            onClick={(e) => {
              lastTrigger.current = e.currentTarget;
              if (window.parent !== window)
                window.parent.postMessage(
                  { type: "kardolive-open-replay", recordingId: r.id },
                  location.origin,
                );
              else setPlaying(r.id);
            }}
          >{tx("Watch replay")}</button>
        </article>
      ))}
      {playing && (
        <AudienceReplayPlayer
          streamKey={streamKey}
          recordingId={playing}
          token={token}
          onClose={() => {
            setPlaying(null);
            lastTrigger.current?.focus();
          }}
        />
      )}
    </section>
  );
}
export function PublicReplayPage() {
  usePageText();
  const { key = "", recordingId = "" } = useParams(),
    [params] = useSearchParams(),
    token = params.get("t") ?? undefined;
  const close = () => {
    if (window.parent !== window)
      window.parent.postMessage(
        { type: "kardolive-close-replay" },
        location.origin,
      );
    else
      location.assign(
        `/watch/?key=${encodeURIComponent(key)}${token ? "&t=" + encodeURIComponent(token) : ""}`,
      );
  };
  return (
    <AudienceReplayPlayer
      streamKey={key}
      recordingId={recordingId}
      token={token}
      onClose={close}
    />
  );
}
